import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { 
  FileText, Loader2, Eye, EyeOff, CheckCircle, Gift, Star,
  Briefcase, Search, DollarSign, Compass, Target, Home, Award,
  Linkedin, Network, Calendar, BookOpen, TrendingUp, Mail, Globe
} from 'lucide-react';
import { authAPI } from '@/lib/api';
import { toast } from 'sonner';
import { initializeCsrf } from '@/utils/csrf';
import LinkedInLoginButton from '@/components/LinkedInLoginButton';
import { trackEvent, identifyUser, EVENTS } from '@/utils/analytics';
import api from '@/lib/api';
import { cn } from '@/lib/utils';

// Tool definitions for display on registration page
const DISPLAY_TOOLS = {
  get_hired: {
    name: "Get Hired (Core)",
    icon: Briefcase,
    color: "from-blue-500 to-blue-600",
    tools: [
      { name: "Resume Hub", description: "Build & upload resumes", icon: FileText, free: true },
      { name: "Search Jobs", description: "Find opportunities", icon: Search, free: true },
      { name: "Interview Prep", description: "Practice with AI", icon: FileText, free: true },
      { name: "Job Tracker", description: "Track applications", icon: Briefcase, free: false },
      { name: "ATS Resume Check", description: "Check compatibility", icon: Target, free: false },
      { name: "Job Board Hub", description: "Find trusted job boards", icon: Globe, free: false },
    ]
  },
  career_strategy: {
    name: "Career Strategy",
    icon: Target,
    color: "from-purple-500 to-purple-600",
    tools: [
      { name: "Career Assessments", description: "Discover interests", icon: Compass, free: true },
      { name: "Career Plan", description: "Your roadmap", icon: Target, free: true },
      { name: "Remote Work Center", description: "WFH opportunities", icon: Home, free: true },
      { name: "Salary Analyzer", description: "Salary insights", icon: DollarSign, free: false },
      { name: "Skills Gap Analysis", description: "Identify skill gaps", icon: TrendingUp, free: false },
      { name: "Career Boost Finder", description: "Certifications & more", icon: Award, free: false },
    ]
  },
  professional_brand: {
    name: "Professional Brand",
    icon: Star,
    color: "from-amber-500 to-orange-500",
    tools: [
      { name: "LinkedIn Optimizer", description: "Optimize profile", icon: Linkedin, free: false },
      { name: "Networking Tracker", description: "Manage network", icon: Network, free: false },
      { name: "Share & Earn", description: "Refer & earn", icon: Gift, free: false },
    ]
  },
  learning: {
    name: "Learning",
    icon: BookOpen,
    color: "from-green-500 to-emerald-600",
    tools: [
      { name: "My Events", description: "Events & webinars", icon: Calendar, free: true },
      { name: "Event Calendar", description: "Visual calendar", icon: Calendar, free: true },
      { name: "GED Pathway", description: "Educational programs", icon: BookOpen, free: true },
    ]
  }
};

// Tool Card Component for sidebar display
const ToolPreviewCard = ({ name, description, icon: Icon, free, color }) => (
  <div className={cn(
    "flex items-center gap-3 p-3 rounded-lg border bg-white/80 backdrop-blur-sm transition-all hover:shadow-md",
    free ? "border-gray-200" : "border-purple-200"
  )}>
    <div className={cn(
      "w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0",
      free ? "bg-gradient-to-br from-gray-100 to-gray-200" : "bg-gradient-to-br from-purple-100 to-pink-100"
    )}>
      <Icon className={cn("w-4 h-4", free ? "text-gray-600" : "text-purple-600")} />
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2">
        <p className="font-medium text-sm text-gray-900 truncate">{name}</p>
        {!free && (
          <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-purple-50 text-purple-600 border-purple-200">
            Premium
          </Badge>
        )}
      </div>
      <p className="text-xs text-gray-500 truncate">{description}</p>
    </div>
  </div>
);

// Category Section for sidebar
const CategoryPreview = ({ category, data }) => (
  <div className="mb-4">
    <div className="flex items-center gap-2 mb-2">
      <data.icon className="w-4 h-4 text-gray-500" />
      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{data.name}</h4>
    </div>
    <div className="space-y-2">
      {data.tools.map((tool, idx) => (
        <ToolPreviewCard key={idx} {...tool} />
      ))}
    </div>
  </div>
);

const Register = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, text: '', color: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [subscribeNewsletter, setSubscribeNewsletter] = useState(false);
  
  // Referral tracking
  const [referralCode, setReferralCode] = useState('');
  const [referralInfo, setReferralInfo] = useState(null);
  const [validatingReferral, setValidatingReferral] = useState(false);

  // Affiliate tracking
  const [affiliateCode, setAffiliateCode] = useState('');
  const [affiliateInfo, setAffiliateInfo] = useState(null);
  const [validatingAffiliate, setValidatingAffiliate] = useState(false);

  // Check for referral code in sessionStorage on mount (set by /referral/:code redirect)
  useEffect(() => {
    const refCode = sessionStorage.getItem('referral_code');
    if (refCode) {
      setReferralCode(refCode);
      validateReferralCode(refCode);
    }
    
    // Check for affiliate code in sessionStorage (set by /affiliate/:code redirect)
    const affCode = sessionStorage.getItem('affiliate_code');
    if (affCode) {
      setAffiliateCode(affCode);
      validateAffiliateCode(affCode);
    }
  }, []);

  const validateReferralCode = async (code) => {
    if (!code) {
      setReferralInfo(null);
      return;
    }
    
    setValidatingReferral(true);
    try {
      const response = await api.get(`/referrals/validate/${code}`);
      if (response.data.valid) {
        setReferralInfo(response.data);
        // Track referral link click
        await api.post(`/referrals/track/${code}`).catch(() => {});
      } else {
        setReferralInfo(null);
      }
    } catch (e) {
      setReferralInfo(null);
    } finally {
      setValidatingReferral(false);
    }
  };

  const validateAffiliateCode = async (code) => {
    if (!code) {
      setAffiliateInfo(null);
      return;
    }
    
    setValidatingAffiliate(true);
    try {
      const response = await api.get(`/affiliates/validate/${code}`);
      if (response.data.valid) {
        setAffiliateInfo(response.data);
      } else {
        setAffiliateInfo(null);
      }
    } catch (e) {
      setAffiliateInfo(null);
    } finally {
      setValidatingAffiliate(false);
    }
  };

  // Check password strength
  const checkPasswordStrength = (password) => {
    let score = 0;
    if (password.length >= 8) score++;
    if (password.match(/[a-z]/) && password.match(/[A-Z]/)) score++;
    if (password.match(/[0-9]/)) score++;
    if (password.match(/[^a-zA-Z0-9]/)) score++;

    const levels = [
      { text: 'Too weak', color: 'bg-red-500' },
      { text: 'Weak', color: 'bg-orange-500' },
      { text: 'Fair', color: 'bg-yellow-500' },
      { text: 'Good', color: 'bg-blue-500' },
      { text: 'Strong', color: 'bg-green-500' }
    ];

    setPasswordStrength({
      score,
      text: levels[score].text,
      color: levels[score].color
    });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Invalid email format';
    if (!formData.password) newErrors.password = 'Password is required';
    else if (formData.password.length < 8) newErrors.password = 'Password must be at least 8 characters';
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    if (!agreedToTerms) newErrors.terms = 'You must agree to the terms';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      await initializeCsrf();
      const response = await authAPI.register({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        subscribe_newsletter: subscribeNewsletter,
        referral_code: referralCode || undefined,
        affiliate_code: affiliateCode || undefined
      });

      if (response.data) {
        // Track registration event
        trackEvent(EVENTS.USER_REGISTERED, {
          method: 'email',
          subscribed_newsletter: subscribeNewsletter,
          referred: !!referralCode,
          affiliate: !!affiliateCode
        });

        if (response.data.user) {
          identifyUser(response.data.user.id, {
            email: response.data.user.email,
            name: response.data.user.name,
            createdAt: new Date().toISOString()
          });
        }

        // Store token and user data
        if (response.data.access_token) {
          localStorage.setItem('access_token', response.data.access_token);
        }
        if (response.data.user) {
          localStorage.setItem('user', JSON.stringify(response.data.user));
        }

        // Clear referral and affiliate codes from session
        sessionStorage.removeItem('referral_code');
        sessionStorage.removeItem('affiliate_code');

        toast.success('Account created successfully!');
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Registration error:', error);
      const message = error.response?.data?.detail || 'Registration failed. Please try again.';
      toast.error(message);
      setErrors({ submit: message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-orange-50 flex items-center justify-center p-4 sm:p-6">
      <div className="w-full max-w-7xl flex gap-6">
        
        {/* Left Sidebar - Tools Preview (Hidden on mobile) */}
        <div className="hidden lg:block w-72 flex-shrink-0">
          <div className="sticky top-6">
            <CategoryPreview category="get_hired" data={DISPLAY_TOOLS.get_hired} />
            <CategoryPreview category="career_strategy" data={DISPLAY_TOOLS.career_strategy} />
          </div>
        </div>

        {/* Center - Registration Form */}
        <div className="flex-1 max-w-md mx-auto pt-2 sm:pt-4">
          {/* Logo */}
          <div className="text-center mb-4 sm:mb-5">
            <div className="inline-flex items-center justify-center mb-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_role-expiry-dash/artifacts/swo6wssk_career_support_80px_transparent_EXACT.png" 
                alt="Career Support" 
                className="h-28 sm:h-36 w-auto"
              />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold mb-2">Start Your Journey</h1>
            <p className="text-sm sm:text-base text-gray-600">Create your free account today</p>
          </div>

          {/* Referral Banner - Enhanced with clear free account + premium discount messaging */}
          {referralInfo && (
            <div className="mb-6 p-5 bg-gradient-to-r from-purple-50 via-pink-50 to-purple-50 rounded-xl border-2 border-purple-200 shadow-sm" data-testid="referral-banner">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0 shadow-lg">
                  <Gift className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-lg font-semibold text-gray-900 mb-1">
                    🎉 You&apos;re here by invitation
                  </p>
                  <p className="text-gray-700">
                    <span className="font-semibold text-purple-700">{referralInfo.referrer_name}</span> invited you to join CareerSupport — a platform built to help you explore careers, strengthen your resume, and move forward with confidence.
                  </p>
                  <div className="mt-3 p-3 bg-white rounded-lg border border-purple-100">
                    <div className="flex items-center gap-2 mb-1">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span className="text-sm font-medium text-gray-900">Sign up is FREE</span>
                    </div>
                    <p className="text-sm text-gray-600 ml-6">
                      Access 7+ free tools instantly. When you&apos;re ready to upgrade to Premium, you&apos;ll get <span className="font-bold text-purple-600">20% off your first payment</span>.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Affiliate Banner - For users coming through affiliate links */}
          {affiliateInfo && !referralInfo && (
            <div className="mb-6 p-5 bg-gradient-to-r from-sky-50 via-blue-50 to-sky-50 rounded-xl border-2 border-sky-200 shadow-sm" data-testid="affiliate-banner">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center flex-shrink-0 shadow-lg">
                  <Star className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-lg font-semibold text-gray-900 mb-1">
                    🌟 Welcome to CareerSupport!
                  </p>
                  <p className="text-gray-700">
                    You&apos;ve been referred by <span className="font-semibold text-sky-700">{affiliateInfo.affiliate_name}</span> — one of our trusted partners helping people advance their careers.
                  </p>
                  <div className="mt-3 p-3 bg-white rounded-lg border border-sky-100">
                    <div className="flex items-center gap-2 mb-1">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span className="text-sm font-medium text-gray-900">Sign up is FREE</span>
                    </div>
                    <p className="text-sm text-gray-600 ml-6">
                      Get instant access to 7+ free career tools. Upgrade anytime to unlock all 20+ premium features.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Register Form */}
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl p-6 sm:p-8 border border-gray-200">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="name" className="text-sm font-medium mb-2 block">
                  Full Name
                </Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="John Smith"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className={errors.name ? 'border-red-500' : ''}
                />
                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
              </div>

              <div>
                <Label htmlFor="email" className="text-sm font-medium mb-2 block">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="john@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className={errors.email ? 'border-red-500' : ''}
                />
                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
              </div>

              <div>
                <Label htmlFor="password" className="text-sm font-medium mb-2 block">
                  Password
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Create a strong password"
                    value={formData.password}
                    onChange={(e) => {
                      setFormData({...formData, password: e.target.value});
                      checkPasswordStrength(e.target.value);
                    }}
                    className={errors.password ? 'border-red-500 pr-10' : 'pr-10'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {formData.password && (
                  <div className="mt-2">
                    <div className="flex gap-1 h-1">
                      {[1, 2, 3, 4].map((level) => (
                        <div
                          key={level}
                          className={`flex-1 rounded-full ${
                            passwordStrength.score >= level ? passwordStrength.color : 'bg-gray-200'
                          }`}
                        />
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{passwordStrength.text}</p>
                  </div>
                )}
                {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
              </div>

              <div>
                <Label htmlFor="confirmPassword" className="text-sm font-medium mb-2 block">
                  Confirm Password
                </Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Confirm your password"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                    className={errors.confirmPassword ? 'border-red-500 pr-10' : 'pr-10'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {errors.confirmPassword && <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>}
              </div>

              {/* Terms and Newsletter */}
              <div className="space-y-3 pt-2">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    className="mt-1 rounded border-gray-300"
                  />
                  <span className="text-sm text-gray-600">
                    I agree to the{' '}
                    <Link to="/terms" className="text-sky-600 hover:underline">Terms of Service</Link>
                    {' '}and{' '}
                    <Link to="/privacy" className="text-sky-600 hover:underline">Privacy Policy</Link>
                  </span>
                </label>
                {errors.terms && <p className="text-red-500 text-xs">{errors.terms}</p>}

                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={subscribeNewsletter}
                    onChange={(e) => setSubscribeNewsletter(e.target.checked)}
                    className="mt-1 rounded border-gray-300"
                  />
                  <span className="text-sm text-gray-600">
                    Subscribe to our bi-weekly career tips newsletter
                  </span>
                </label>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-sky-500 to-orange-500 hover:from-sky-600 hover:to-orange-600 text-white py-3 rounded-lg font-semibold"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating account...
                  </>
                ) : (
                  'Create Free Account'
                )}
              </Button>

              {/* OR Divider */}
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-gray-400">or continue with</span>
                </div>
              </div>

              {/* Social Login */}
              <LinkedInLoginButton referralCode={referralCode} affiliateCode={affiliateCode} />
            </form>

            {/* Sign In Link */}
            <p className="text-center text-sm text-gray-600 mt-6">
              Already have an account?{' '}
              <Link to="/login" className="font-semibold text-sky-600 hover:underline">
                Sign in
              </Link>
            </p>
          </div>

          {/* Tools Preview on Mobile */}
          <div className="lg:hidden mt-8">
            <h3 className="text-center text-sm font-semibold text-gray-500 mb-4">TOOLS YOU&apos;LL GET ACCESS TO</h3>
            <div className="grid grid-cols-2 gap-2">
              {DISPLAY_TOOLS.get_hired.tools.slice(0, 4).map((tool, idx) => (
                <ToolPreviewCard key={idx} {...tool} />
              ))}
            </div>
          </div>
        </div>

        {/* Right Sidebar - Tools Preview (Hidden on mobile) */}
        <div className="hidden lg:block w-72 flex-shrink-0">
          <div className="sticky top-6">
            <CategoryPreview category="professional_brand" data={DISPLAY_TOOLS.professional_brand} />
            <CategoryPreview category="learning" data={DISPLAY_TOOLS.learning} />
            
            {/* Premium CTA */}
            <div className="mt-4 p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border border-purple-200">
              <div className="flex items-center gap-2 mb-2">
                <Star className="w-5 h-5 text-purple-500" />
                <span className="font-semibold text-purple-800">Premium Tools</span>
              </div>
              <p className="text-sm text-purple-700 mb-3">
                Unlock all 20+ tools with Premium. Start free, upgrade anytime.
              </p>
              <div className="flex items-center gap-2 text-xs text-purple-600">
                <CheckCircle className="w-4 h-4" />
                <span>7+ Free tools included</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
